import { noop } from 'lodash-es';
import React from 'react';
import styled, { ThemeProvider, css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { BD_ACCESS_PORTAL_DOCS } from 'constants/products';
import * as utils from '../../../utils';

import FjSideNavItem from '../fj-side-nav-item';

// Injected by Vite from the workspace root manifest; see src/types/build-constants.d.ts.
const version = __APP_VERSION__;

// The custom side-nav theme supplied via the `style` prop. It does not match the
// app-wide DefaultTheme, so styled-component interpolations narrow to this shape.
interface SideNavTheme {
  primaryColor: string;
  backgroundColor: string;
  width: string;
  titleFontSize: string;
  titleHeight: string;
  iconBoxHeight: string;
  iconSize: string;
  colors: { white: string };
}

interface SideNavIconProps {
  size?: string;
  active?: boolean;
  [key: string]: unknown;
}

interface SideNavItemProps {
  id: string;
  title?: string;
  titleFontSizeOverride?: string;
  icon: React.ReactElement<SideNavIconProps>;
  'data-testid'?: string;
}

interface FjSideNavProps {
  children: React.ReactElement<SideNavItemProps>[];
  activeTab: string;
  onTabSelected: (id: string) => void;
  style: SideNavTheme;
}

const chinaFlag = utils.chinaFlag();

class FjSideNav extends React.Component<FjSideNavProps> {
  static defaultProps: Partial<FjSideNavProps> = {
    children: [],
    activeTab: '',
    onTabSelected: noop,
  };

  // Only render FjSideNavItem children
  get validChildren(): React.ReactElement<SideNavItemProps>[] {
    return this.props.children.filter(
      (child) =>
        child.type === FjSideNavItem ||
        (child.type as unknown as { prototype: unknown }).prototype instanceof FjSideNavItem,
    );
  }

  get activeItemIndex(): number | null {
    const index = this.validChildren.findIndex((child) => this.props.activeTab === child.props.id);
    return index < 0 ? null : index;
  }

  handleSelectTab(id: string): void {
    this.props.onTabSelected(id);
  }

  renderToolbarItems(): React.ReactNode[] {
    return this.validChildren.map((child) => {
      const childSize = child.props.icon.props.size;
      const isCustomIconSize = childSize != null && childSize !== '1em';
      const iconProps = {
        ...child.props.icon.props,
        active: this.props.activeTab === child.props.id,
        // onClick: () => this.handleSelectTab(child.props.id),
        size: isCustomIconSize ? childSize : this.props.style.iconSize,
      };
      const Icon = React.cloneElement(
        child.props.icon,
        iconProps,
        (child.props.icon as unknown as { children?: React.ReactNode }).children,
      );
      return (
        <IconContainer key={child.props.id}>
          <IconInnerContainer
            onClick={() => this.handleSelectTab(child.props.id)}
            data-testid={child.props['data-testid']}
          >
            {Icon}
          </IconInnerContainer>
        </IconContainer>
      );
    });
  }

  renderTitleText(): React.ReactNode {
    const activeChild = this.validChildren.find((child) => this.props.activeTab === child.props.id);
    if (activeChild) {
      const titleText = activeChild.props.title || '';
      return (
        <SidebarTitleText fontSizeOverride={activeChild.props.titleFontSizeOverride}>
          {titleText}
        </SidebarTitleText>
      );
    }
    return null;
  }

  render(): React.ReactNode {
    return (
      // The `style` prop is a custom side-nav theme that is intentionally used in
      // place of the app-wide DefaultTheme for this subtree.
      <ThemeProvider theme={this.props.style as unknown as DefaultTheme}>
        <SidebarWrapper>
          <SidebarTitle>{this.renderTitleText()}</SidebarTitle>
          <SidebarContent>
            {this.renderToolbarItems()}
            <SelectionBar selectionBarIndex={this.activeItemIndex} />
          </SidebarContent>
          {!chinaFlag && (
            <SidebarFormerlyFlowJoPortal>
              <SidebarAnchor href={BD_ACCESS_PORTAL_DOCS}>
                FlowJo Portal <br />
                is now
                <br />
                BD Access Portal <br />
              </SidebarAnchor>
            </SidebarFormerlyFlowJoPortal>
          )}
          {chinaFlag && (
            <SidebarFormerlyFlowJoPortal>
              <SidebarAnchor href={BD_ACCESS_PORTAL_DOCS}>
                FlowJo Portal <br />
                现更名为
                <br />
                BD Access Portal <br />{' '}
              </SidebarAnchor>
            </SidebarFormerlyFlowJoPortal>
          )}
          <SidebarFooter>
            <SidebarAnchor href="/version">v{version}</SidebarAnchor>
          </SidebarFooter>
        </SidebarWrapper>
      </ThemeProvider>
    );
  }
}

export default FjSideNav;

// The provided theme is the custom SideNavTheme rather than the app DefaultTheme.
const themeOf = (props: { theme: unknown }): SideNavTheme => props.theme as SideNavTheme;

const iconMargin = css`
  calc(calc(${(props) => themeOf(props).iconBoxHeight} - ${(props) => themeOf(props).iconSize}) / 2)
`;

const SidebarFormerlyFlowJoPortal = styled.div`
  padding: 4px 8px 0px 8px;
  font-size: 10px;
  font-weight: 500;
  text-align: center;
  color: ${(props) => themeOf(props).colors.white};
`;

const SidebarWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: ${(props) => themeOf(props).width};
  height: 100%;
  min-height: fit-content;
  background-color: ${(props) => themeOf(props).backgroundColor};
`;

const SidebarTitle = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: ${(props) => themeOf(props).titleHeight};
`;

const SidebarTitleText = styled.div<{ fontSizeOverride?: string }>`
  display: inline-block;
  font-size: ${(props) => props.fontSizeOverride || themeOf(props).titleFontSize};
  font-weight: bold;
  text-align: center;
  text-transform: uppercase;
  color: ${(props) => themeOf(props).primaryColor};
`;

const SidebarContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  align-items: center;
  width: ${(props) => themeOf(props).width};
  transform: translateY(calc(-1 * ${iconMargin}));
  min-height: fit-content;
`;

const SidebarFooter = styled.div`
  display: flex;
  flex-grow: 0;
  justify-content: center;
  align-items: center;
  height: 40px;
  bottom: 0;
  width: 100%;
  color: ${(props) => themeOf(props).colors.white};
`;

const SidebarAnchor = styled.a`
  color: ${(props) => themeOf(props).colors.white};
  text-decoration: none;
  position: relative;
`;

const IconContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${(props) => themeOf(props).width};
  height: ${(props) => themeOf(props).iconBoxHeight};

  svg {
    cursor: pointer;
  }
`;

const IconInnerContainer = styled.button.attrs<{ 'data-testid'?: string }>(
  ({ 'data-testid': dataTestId = 'icon-inner-container' }) => ({
    'data-testid': dataTestId,
  }),
)`
  border: 0;
  padding: 0;
  margin: 0;
  background: none;
`;

const SelectionBar = styled.div<{ selectionBarIndex: number | null }>`
  visibility: ${(props) => (props.selectionBarIndex === null ? 'hidden' : 'visible')};
  position: absolute;
  left: 0px;
  top: calc(${(props) => props.selectionBarIndex || 0} * ${(props) => themeOf(props).iconBoxHeight});
  height: calc(calc(${(props) => themeOf(props).iconSize}) + 10px);
  transform: translateY(calc(${iconMargin} - 5px));
  width: 5px;
  background-color: ${(props) => themeOf(props).primaryColor};
  transition: top 0.14s;
  transition-timing-function: ease;
`;
